import React, { useState, useEffect } from "react";
import { Calendar, dateFnsLocalizer, View, Views } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import { enGB } from "date-fns/locale";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { parseGeoCoordinates } from "@/utils/geo";
import { LoadingScreen } from "../ui/LoadingScreen";
import { Card } from "@radix-ui/themes";

// Define the event interface that our calendar will use
export interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay?: boolean;
  description?: string;
  location?: string;
  resource?: Record<string, unknown>;
  [key: string]: unknown; // Allow additional fields
}

// Configure localizer with UK settings
const localizer = dateFnsLocalizer({
  format: (date: Date, formatStr: string) =>
    format(date, formatStr, { locale: enGB }),
  parse: (str: string, formatStr: string) =>
    parse(str, formatStr, new Date(), { locale: enGB }),
  startOfWeek: () => startOfWeek(new Date(), { locale: enGB }),
  getDay,
  locales: { "en-GB": enGB },
});

// Fields that should always be hidden from events
const HIDDEN_FIELDS = [
  "geo",
  "tenant_id",
  "_customStyles",
  "_customClasses",
  "_directClickHandler",
  "EntityName",
  "onClick",
];

interface CalendarViewProps {
  data: Record<string, unknown>[];
  isLoading?: boolean;
  startDateField: string;
  endDateField?: string;
  titleField: string;
  descriptionField?: string;
  locationField?: string;
  geoField?: string;
  onEventClick?: (event: CalendarEvent) => void;
  className?: string;
  entityName?: string;
}

// Custom event component that displays fields based on selection
const EventComponent = ({ event }: { event: CalendarEvent }) => {
  return (
    <div className="h-full overflow-hidden text-white p-1">
      <div className="text-xs font-medium truncate">{event.title}</div>
    </div>
  );
};

const AgendaEventComponent = ({ event }: { event: CalendarEvent }) => {
  return (
    <div className="py-1" style={{ color: "#000" }}>
      <div
        className="font-medium text-sm text-blue-600"
        style={{ color: "#2563eb" }}
      >
        {event.title}
      </div>
      {event.description && (
        <div
          className="text-xs mt-1 text-gray-600"
          style={{ color: "#4b5563" }}
        >
          {event.description}
        </div>
      )}
      {event.location && (
        <div
          className="text-xs mt-1 flex items-center gap-1 text-gray-500"
          style={{ color: "#6b7280" }}
        >
          <span className="inline-block mr-1">📍</span>
          {event.location}
        </div>
      )}
    </div>
  );
};

export default function CalendarView({
  data = [],
  isLoading = false,
  startDateField,
  endDateField,
  titleField,
  descriptionField,
  locationField,
  geoField,
  onEventClick,
  entityName = "",
}: CalendarViewProps) {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [view, setView] = useState<View>(Views.MONTH);
  const [date, setDate] = useState(new Date());

  useEffect(() => {
    if (!data || !startDateField) return;

    // Convert raw data into calendar events
    const calendarEvents = data
      .map((item) => {
        const startDate = item[startDateField];
        if (!startDate) return null;

        // Parse start date
        let start;
        try {
          start = new Date(String(startDate));
          if (isNaN(start.getTime())) return null;
        } catch (error) {
          console.error("Failed to parse start date:", startDate, error);
          return null;
        }

        // Parse end date or default to start + 1 hour
        let endDate;
        try {
          if (endDateField && item[endDateField]) {
            endDate = new Date(String(item[endDateField]));
            if (isNaN(endDate.getTime())) {
              endDate = new Date(
                new Date(start).setHours(start.getHours() + 1)
              );
            }
          } else {
            endDate = new Date(new Date(start).setHours(start.getHours() + 1));
          }

          // Ensure events have at least 30 minutes duration
          if (endDate.getTime() - start.getTime() < 30 * 60 * 1000) {
            endDate.setTime(start.getTime() + 30 * 60 * 1000);
          }
        } catch (error) {
          console.error("Failed to process end date, using default:", error);
          endDate = new Date(new Date(start).setHours(start.getHours() + 1));
        }

        // Determine if all-day event
        const allDay =
          (start.getHours() === 0 &&
            start.getMinutes() === 0 &&
            endDate.getHours() === 0 &&
            endDate.getMinutes() === 0) ||
          start.getDate() !== endDate.getDate();

        // Create event object
        const event: CalendarEvent = {
          id: String(item.id || ""),
          title: item[titleField]?.toString() || "Untitled Event",
          start,
          end: endDate,
          allDay,
          description:
            descriptionField && item[descriptionField]
              ? String(item[descriptionField])
              : undefined,
          location:
            locationField && item[locationField]
              ? String(item[locationField])
              : undefined,
          resource: { ...item },
        };

        // Add location from geo if available
        if (!event.location && geoField && item[geoField]) {
          const coords = parseGeoCoordinates(item[geoField] as string);
          if (coords) {
            event.location = `${coords.lat.toFixed(6)}, ${coords.lng.toFixed(
              6
            )}`;
          }
        }

        // Add additional fields for display
        for (const [key, value] of Object.entries(item)) {
          if (!HIDDEN_FIELDS.includes(key) && key !== "id") {
            event[key] = value;
          }
        }

        return event;
      })
      .filter(Boolean) as CalendarEvent[];

    setEvents(calendarEvents);
  }, [
    data,
    startDateField,
    endDateField,
    titleField,
    descriptionField,
    locationField,
    geoField,
    entityName,
  ]);

  // Custom event style - simplified
  const eventStyleGetter = () => ({
    style: {
      backgroundColor: "#3b82f6",
      borderRadius: "4px",
      color: "white",
    },
  });

  // Simplified tooltip generator
  const eventTooltipAccessor = (event: CalendarEvent): string => {
    const lines = [event.title];

    if (event.description) lines.push(event.description);
    if (event.location) lines.push(`Location: ${event.location}`);

    return lines.join("\n");
  };

  if (isLoading) {
    return <LoadingScreen fullPage={false} />;
  }

  if (!events.length) {
    return (
      <Card>
        No events found. Check that your data contains valid date fields.
      </Card>
    );
  }

  return (
    <Card>
      <div className="h-full calendar-container">
        <style>{`
            /* Fix flex layout for calendar container */
            .rbc-calendar {
              display: flex;
              flex-direction: column;
              min-height: 580px;
            }

            /* Make sure agenda view takes proper space */
            .rbc-calendar.rbc-agenda-view {
              height: auto !important;
            }

            /* Core event styles */
            .rbc-event {
              min-height: 24px;
              height: auto !important;
              display: flex;
              flex-direction: column;
              z-index: 1;
              position: relative;
              background-color: #3b82f6;
              border-radius: 4px;
              color: white;
              padding: 4px;
            }

            /* Month view styles */
            .rbc-month-view .rbc-month-row {
              min-height: 120px;
            }

            .rbc-month-view .rbc-event {
              min-height: 24px;
              max-height: 80px;
              overflow: hidden;
            }

            /* Navigation and controls */
            .rbc-toolbar button {
              cursor: pointer !important;
              z-index: 5;
              position: relative;
            }

            .rbc-toolbar button.rbc-active {
              background-color: #3b82f6;
              color: white;
            }

            .rbc-toolbar button:hover {
              background-color: #f3f4f6;
            }

            /* Cell borders */
            .rbc-month-view .rbc-month-row + .rbc-month-row {
              border-top: 1px solid #ddd;
            }

            .rbc-day-bg + .rbc-day-bg {
              border-left: 1px solid #ddd;
            }

            /* Agenda view styles - simplified */
            .rbc-agenda-view {
              border: 1px solid #ddd;
              border-radius: 6px;
              overflow: hidden;
              display: block !important;
              flex: 0 0 auto !important;
            }

            /* Specifically target agenda events to remove background color */
            .rbc-agenda-view .rbc-event,
            .rbc-agenda-event-cell .rbc-event {
              background-color: transparent !important;
              color: inherit !important;
              padding: 0 !important;
              border-radius: 0 !important;
              box-shadow: none !important;
              border: none !important;
            }

            /* Clear any background from the event cells */
            .rbc-agenda-event-cell {
              background-color: transparent !important;
            }

            /* Override cell styles in the row */
            tr[class^="rbc-agenda"] td,
            .rbc-agenda-view tr,
            .rbc-agenda-table tr {
              background-color: white !important;
            }

            /* Ensure all text in agenda is visible */
            .rbc-agenda-view * {
              color: inherit;
            }

            .rbc-agenda-view table {
              border-spacing: 0;
              border-collapse: collapse;
              width: 100%;
            }

            /* Fix flex display that's breaking agenda view */
            .rbc-agenda-content {
              display: block !important;
              flex: none !important;
            }

            .rbc-agenda-view table tbody > tr > td {
              padding: 8px 12px;
              border-bottom: 1px solid #eee;
              vertical-align: middle;
            }

            .rbc-agenda-table {
              width: 100%;
              display: table !important;
            }

            .rbc-agenda-date-cell {
              font-weight: 600;
              color: #374151;
              width: 120px;
              padding-left: 16px !important;
            }

            .rbc-agenda-time-cell {
              width: 120px;
              color: #4b5563;
              font-variant-numeric: tabular-nums;
            }

            .rbc-agenda-event-cell {
              padding-left: 8px !important;
            }

            /* Critical fix for agenda events to be visible */
            .rbc-agenda-view table.rbc-agenda-table tbody > tr > td + td {
              min-width: 100px;
              max-width: none;
              overflow: visible;
              white-space: normal;
            }

            /* Force all cells and rows to have proper background */
            .rbc-agenda-view tbody,
            .rbc-agenda-table tbody,
            .rbc-agenda-table tr,
            .rbc-agenda-table td {
              background-color: white !important;
            }

            /* Set specific background for the event row */
            .rbc-agenda-view table tbody tr {
              background-color: white !important;
            }

            /* Make sure all event containers are visible */
            .rbc-event,
            .rbc-event-content,
            .rbc-event-label {
              overflow: visible !important;
              opacity: 1 !important;
              visibility: visible !important;
              pointer-events: auto !important;
            }

            /* Override flex layout for agenda view specifically */
            .rbc-agenda-view {
              flex: 1 0 0;
              flex-direction: column;
              display: flex !important;
              overflow: auto !important;
            }

            /* Fix flex display for agenda view contents */
            .rbc-agenda-view .rbc-agenda-content {
              flex: 1;
              min-height: 0;
              overflow: auto;
            }

            /* Ensure agenda table has proper layout */
            .rbc-agenda-table {
              width: 100%;
              border-spacing: 0;
              border-collapse: collapse;
            }

            .rbc-agenda-table td {
              padding: 8px;
              vertical-align: top;
            }

            .rbc-agenda-date-cell,
            .rbc-agenda-time-cell {
              font-weight: 500;
              width: 100px;
            }

            .rbc-agenda-event-cell {
              width: auto;
            }
          `}</style>

        {/* @ts-ignore */}
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          titleAccessor="title"
          tooltipAccessor={eventTooltipAccessor}
          onSelectEvent={onEventClick}
          eventPropGetter={eventStyleGetter}
          view={view}
          onView={(newView) => {
            setView(newView);
          }}
          date={date}
          onNavigate={setDate}
          views={["month", "week", "day", "agenda"]}
          defaultView="month"
          length={90}
          messages={{
            noEventsInRange: "No events in selected time range.",
            allDay: "all day",
          }}
          components={{
            event: (props) => (
              <EventComponent event={props.event as CalendarEvent} />
            ),
            agenda: {
              event: ({ event }) => (
                <AgendaEventComponent event={event as CalendarEvent} />
              ),
            },
          }}
          min={new Date(0, 0, 0, 0, 0)}
          max={new Date(0, 0, 0, 23, 59)}
          formats={{
            agendaDateFormat: "EEE MMM d",
            agendaHeaderFormat: ({ start, end }) =>
              `${format(start, "MMM d", { locale: enGB })} - ${format(
                end,
                "MMM d, yyyy",
                { locale: enGB }
              )}`,
            agendaTimeRangeFormat: ({ start, end }) =>
              `${format(start, "h:mm a", { locale: enGB })} – ${format(
                end,
                "h:mm a",
                { locale: enGB }
              )}`,
            agendaTimeFormat: (date) =>
              format(date, "h:mm a", { locale: enGB }),
          }}
          getNow={() => new Date()}
          popup
          showMultiDayTimes
        />
      </div>
    </Card>
  );
}
